/**
 * Authorization Service
 * Runtime permission checking with security version validation and constraint evaluation
 */

import { API, graphqlOperation } from 'aws-amplify';
import { getUserPermissionsCache, userPermissionsCacheByUserId } from '../graphql/queries';
import {
  canAccessLocation as checkAccess,
  filterLocationsByDataScope,
  isScopeUniversal
} from '../utils/permissionHelpers';

// Cache duration (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Cache key prefix
const CACHE_KEY_PREFIX = 'permissions_';

/**
 * Checks if a user has a specific permission
 *
 * @param {string} resource - Resource pattern (e.g., 'ops:report:sales')
 * @param {string} action - Action to check (VIEW, EDIT, CREATE, DELETE, EXPORT, ADMIN)
 * @param {Object} userPermissions - User permissions cache object
 * @returns {boolean} True if user has permission
 */
export function hasPermission(resource, action, userPermissions) {
  if (!userPermissions?.permissions) {
    return false;
  }

  // Build the permission key
  const permissionKey = `${resource}:${action}`;

  // Check exact match first
  if (userPermissions.permissions[permissionKey]) {
    return userPermissions.permissions[permissionKey].allowed === true;
  }

  // Check wildcard patterns
  return checkWildcardPermissions(resource, action, userPermissions.permissions);
}

/**
 * Checks wildcard permissions for a resource and action
 */
function checkWildcardPermissions(resource, action, permissions) {
  const resourceParts = resource.split(':');

  // Patterns to check (from most specific to least specific)
  const patterns = [
    `${resourceParts[0]}:${resourceParts[1]}:*:${action}`,  // ops:report:*:VIEW
    `${resourceParts[0]}:*:${action}`,                      // ops:*:VIEW
    `*:*:${action}`,                                        // *:*:VIEW
    `${resourceParts[0]}:${resourceParts[1]}:${resourceParts[2]}:*`, // ops:report:sales:*
    `${resourceParts[0]}:${resourceParts[1]}:*`,           // ops:report:*
    `${resourceParts[0]}:*`,                                // ops:*
    '*:*'                                                    // Full wildcard
  ];

  for (const pattern of patterns) {
    const permissionKey = `${pattern}:${action}`;
    if (permissions[permissionKey]?.allowed === true) {
      return true;
    }
    // Also check pattern without action for full wildcards
    if (permissions[pattern]?.allowed === true) {
      return true;
    }
  }

  return false;
}

/**
 * Authorizes a user for a specific resource and action
 *
 * @param {Object} user - User object with status and securityVersion
 * @param {Object} userPermissions - User permissions cache
 * @param {string} resource - Resource to access
 * @param {string} action - Action to perform
 * @returns {Object} Authorization result with authorized flag and reason
 */
export async function authorize(user, userPermissions, resource, action) {
  // Check user status
  if (!user) {
    return {
      authorized: false,
      reason: 'NO_USER'
    };
  }

  if (user.status === 'SUSPENDED') {
    return {
      authorized: false,
      reason: 'USER_SUSPENDED'
    };
  }

  if (user.status === 'INACTIVE') {
    return {
      authorized: false,
      reason: 'USER_INACTIVE'
    };
  }

  // Check permissions cache exists
  if (!userPermissions) {
    return {
      authorized: false,
      reason: 'NO_PERMISSIONS_CACHE'
    };
  }

  // Check permission
  const hasAccess = hasPermission(resource, action, userPermissions);

  return {
    authorized: hasAccess,
    reason: hasAccess ? null : 'PERMISSION_DENIED'
  };
}

/**
 * Authorizes with constraint evaluation
 *
 * @param {Object} user - User object
 * @param {Object} userPermissions - User permissions cache
 * @param {string} resource - Resource to access
 * @param {string} action - Action to perform
 * @param {Object} context - Context for constraint evaluation
 * @returns {Object} Authorization result with constraints check
 */
export async function authorizeWithConstraints(user, userPermissions, resource, action, context) {
  // First check basic authorization
  const authResult = await authorize(user, userPermissions, resource, action);

  if (!authResult.authorized) {
    return authResult;
  }

  // Get the specific permission to check constraints
  const permissionKey = `${resource}:${action}`;
  const permission = userPermissions.permissions[permissionKey];

  if (!permission?.constraints) {
    // No constraints, authorization passes
    return authResult;
  }

  // Evaluate constraints
  const constraintResult = await evaluateConstraints(permission.constraints, context);

  if (!constraintResult.passed) {
    return {
      authorized: false,
      reason: 'CONSTRAINT_FAILED',
      failedConstraints: constraintResult.failedConstraints
    };
  }

  return authResult;
}

/**
 * Validates security version between user and cached permissions
 *
 * @param {Object} user - User object with current securityVersion
 * @param {Object} userPermissions - Cached permissions with securityVersion
 * @returns {Object} Validation result
 */
export async function validateSecurityVersion(user, userPermissions) {
  // If either doesn't have security version, allow gracefully
  if (!user?.securityVersion || !userPermissions?.securityVersion) {
    return {
      valid: true,
      needsRefresh: false
    };
  }

  // Check version mismatch
  if (user.securityVersion !== userPermissions.securityVersion) {
    return {
      valid: false,
      needsRefresh: true,
      reason: 'SECURITY_VERSION_MISMATCH',
      userVersion: user.securityVersion,
      cacheVersion: userPermissions.securityVersion
    };
  }

  // Check cache age
  const cacheAge = Date.now() - new Date(userPermissions.calculatedAt).getTime();
  if (cacheAge > CACHE_DURATION) {
    return {
      valid: true,
      needsRefresh: true,
      reason: 'CACHE_TOO_OLD',
      cacheAge: Math.floor(cacheAge / (1000 * 60 * 60)) // hours
    };
  }

  return {
    valid: true,
    needsRefresh: false
  };
}

/**
 * Evaluates constraints against context
 *
 * @param {Object} constraints - Constraints to evaluate
 * @param {Object} context - Context containing values to check
 * @returns {Object} Evaluation result
 */
export async function evaluateConstraints(constraints, context) {
  const failedConstraints = [];

  if (!constraints || Object.keys(constraints).length === 0) {
    return {
      passed: true,
      failedConstraints
    };
  }

  // Evaluate each constraint type
  for (const [constraintType, constraintValue] of Object.entries(constraints)) {
    const passed = evaluateConstraint(constraintType, constraintValue, context);
    if (!passed) {
      failedConstraints.push(constraintType);
    }
  }

  return {
    passed: failedConstraints.length === 0,
    failedConstraints
  };
}

/**
 * Evaluates a single constraint
 */
function evaluateConstraint(type, value, context) {
  switch (type) {
    case 'storeType':
      return value.includes(context.storeType);

    case 'dateRange':
      const currentDate = new Date(context.currentDate || Date.now());
      const fromDate = new Date(value.from);
      const toDate = new Date(value.to);
      return currentDate >= fromDate && currentDate <= toDate;

    case 'salesThreshold':
      const sales = context.salesAmount || 0;
      return sales >= (value.min || 0) && sales <= (value.max || Infinity);

    case 'dayPart':
      return value.includes(context.dayPart);

    case 'frequency':
      return value === context.frequency;

    case 'locationStatus':
      return value.includes(context.locationStatus);

    case 'customRule':
      // Safely evaluate custom rule
      try {
        // Create a safe evaluation context
        const safeContext = { ...context };
        // Use Function constructor for safe evaluation
        const func = new Function('context', `return ${value}`);
        return func(safeContext);
      } catch (error) {
        console.error('Custom rule evaluation failed:', error);
        return false;
      }

    default:
      // Unknown constraint type - fail safe
      return false;
  }
}

/**
 * Checks if user can access a specific location
 *
 * @param {string} locationId - Location ID to check
 * @param {Object} userPermissions - User permissions with dataScope
 * @returns {boolean} True if user can access location
 */
export function canAccessLocation(locationId, userPermissions) {
  if (!userPermissions?.dataScope) {
    return false;
  }

  return checkAccess(locationId, userPermissions.dataScope);
}

/**
 * Checks if user can edit a specific location
 *
 * @param {string} locationId - Location ID to check
 * @param {Object} userPermissions - User permissions with dataScope
 * @returns {boolean} True if user can edit location
 */
export function canEditLocation(locationId, userPermissions) {
  if (!userPermissions?.dataScope) {
    return false;
  }

  // In new model, edit permissions are action-based (handled by grants)
  // Data scope only controls visibility
  // So canEdit = canAccess for data scope purposes
  return checkAccess(locationId, userPermissions.dataScope);
}

/**
 * Filters data based on user's data scope
 *
 * @param {Array} data - Array of data items to filter
 * @param {Object} dataScope - User's data scope
 * @returns {Array} Filtered data
 */
export function filterByDataScope(data, dataScope) {
  if (!dataScope || !data || data.length === 0) {
    return [];
  }

  // For UNIVERSAL scope, return all data
  if (isScopeUniversal(dataScope.scopeType)) {
    return data;
  }

  // Use permissionHelpers to filter
  // Map data items to location objects format
  const locationObjects = data.map(item => ({
    id: item.id || item.storeId || item.locationId,
    _originalItem: item
  }));

  // Filter using permissionHelpers
  const filteredLocationObjects = filterLocationsByDataScope(locationObjects, dataScope);

  // Extract original items
  return filteredLocationObjects.map(locObj => locObj._originalItem);
}

/**
 * Fetches user permissions from cache or API
 *
 * @param {string} userId - User ID
 * @param {string} clientId - Client ID (REQUIRED for security - prevents unauthorized data access)
 * @param {boolean} forceRefresh - Force fetch from API
 * @returns {Promise<Object>} User permissions
 */
export async function fetchUserPermissions(userId, clientId, forceRefresh = false) {
  const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;

  console.log('[authorizationService] fetchUserPermissions called:', { userId, clientId, forceRefresh });

  // SECURITY: clientId is required to prevent unauthorized data access
  if (!clientId) {
    console.error('[authorizationService] SECURITY ERROR: clientId is required but was not provided');
    throw new Error('clientId is required for security - cannot fetch permissions without it');
  }

  // Check local storage cache first
  if (!forceRefresh) {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const permissions = JSON.parse(cached);

        // Verify cached permissions match the requested clientId (security check)
        if (permissions.clientId !== clientId) {
          console.warn('[authorizationService] Cached permissions clientId mismatch - clearing cache', {
            cached: permissions.clientId,
            requested: clientId
          });
          localStorage.removeItem(cacheKey);
        } else {
          // Check if cache is still valid
          const cacheAge = Date.now() - new Date(permissions.calculatedAt).getTime();
          if (cacheAge < CACHE_DURATION) {
            console.log('[authorizationService] Returning cached permissions from localStorage');
            return permissions;
          } else {
            console.log('[authorizationService] LocalStorage cache expired, age (hours):', Math.floor(cacheAge / (1000 * 60 * 60)));
          }
        }
      } else {
        console.log('[authorizationService] No permissions in localStorage');
      }
    } catch (error) {
      console.error('[authorizationService] Error reading permissions cache:', error);
    }
  }

  // Fetch from API using composite key (clientId#userId)
  try {
    const compositeKey = `${clientId}#${userId}`;
    console.log('[authorizationService] Fetching with composite key:', compositeKey);

    const response = await API.graphql(
      graphqlOperation(getUserPermissionsCache, { id: compositeKey })
    );

    console.log('[authorizationService] Query response:', response.data);

    const permissions = response.data.getUserPermissionsCache;

    if (permissions) {
      console.log('[authorizationService] Permissions found in database:', {
        id: permissions.id,
        userId: permissions.userId,
        clientId: permissions.clientId,
        calculatedAt: permissions.calculatedAt,
        securityVersion: permissions.securityVersion,
        hasNavigation: !!permissions.navigation,
        hasDataScope: !!permissions.dataScope
      });

      // Parse JSON fields only if they're strings
      // Note: AWSJSON fields come back from AppSync already parsed by DynamoDB/AppSync layer
      try {
        if (typeof permissions.permissions === 'string') {
          permissions.permissions = JSON.parse(permissions.permissions);
        }
      } catch (error) {
        console.error('[authorizationService] Error parsing permissions field:', error);
      }

      try {
        if (typeof permissions.navigation === 'string') {
          permissions.navigation = JSON.parse(permissions.navigation);
        }
      } catch (error) {
        console.error('[authorizationService] Error parsing navigation field:', error);
      }

      // dataScope is a structured type (not AWSJSON), so it should already be an object
      try {
        if (typeof permissions.dataScope === 'string') {
          permissions.dataScope = JSON.parse(permissions.dataScope);
        }
      } catch (error) {
        console.error('[authorizationService] Error parsing dataScope field:', error);
      }

      // Cache in localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify(permissions));
        console.log('[authorizationService] Cached permissions in localStorage');
      } catch (error) {
        console.error('[authorizationService] Error caching permissions:', error);
      }

      return permissions;
    }

    console.log('[authorizationService] No permissions found in database - returning null');
    return null;
  } catch (error) {
    console.error('[authorizationService] Error fetching user permissions:', error);
    throw error;
  }
}

/**
 * Clears cached permissions for a user
 *
 * @param {string} userId - User ID
 */
export function clearPermissionsCache(userId) {
  const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;

  try {
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing permissions cache:', error);
  }
}

/**
 * Clears user permissions from both DynamoDB and localStorage
 * Handles gracefully if permissions don't exist in either location
 *
 * @param {string} userId - User ID
 * @param {string} clientId - Client ID
 * @returns {Promise<Object>} Result object with success status
 */
export async function clearUserPermissions(userId, clientId) {
  console.log('[authorizationService] clearUserPermissions called:', { userId, clientId });

  const results = {
    success: true,
    clearedDynamoDB: false,
    clearedLocalStorage: false,
    errors: []
  };

  // SECURITY: clientId is required
  if (!clientId) {
    console.error('[authorizationService] SECURITY ERROR: clientId is required for clearUserPermissions');
    results.success = false;
    results.errors.push('clientId is required for security');
    return results;
  }

  // 1. Clear from DynamoDB
  try {
    const compositeKey = `${clientId}#${userId}`;
    console.log('[authorizationService] Attempting to delete from DynamoDB:', compositeKey);

    const { deleteUserPermissionsCache } = await import('../graphql/mutations');
    const { API, graphqlOperation } = await import('aws-amplify');

    await API.graphql(
      graphqlOperation(deleteUserPermissionsCache, {
        input: { id: compositeKey }
      })
    );

    console.log('[authorizationService] Successfully deleted from DynamoDB');
    results.clearedDynamoDB = true;
  } catch (error) {
    // Check if error is "not found" - that's OK
    if (error.errors?.[0]?.errorType === 'DynamoDB:ConditionalCheckFailedException' ||
        error.errors?.[0]?.message?.includes('not found') ||
        error.message?.includes('not found')) {
      console.log('[authorizationService] No DynamoDB record found (already cleared or never existed)');
      results.clearedDynamoDB = false;
    } else {
      console.error('[authorizationService] Error deleting from DynamoDB:', error);
      results.errors.push(`DynamoDB error: ${error.message}`);
      // Don't fail the whole operation - still try to clear localStorage
    }
  }

  // 2. Clear from localStorage
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
    const existedInCache = localStorage.getItem(cacheKey) !== null;

    localStorage.removeItem(cacheKey);

    if (existedInCache) {
      console.log('[authorizationService] Successfully cleared from localStorage');
      results.clearedLocalStorage = true;
    } else {
      console.log('[authorizationService] No localStorage entry found (already cleared or never existed)');
      results.clearedLocalStorage = false;
    }
  } catch (error) {
    console.error('[authorizationService] Error clearing localStorage:', error);
    results.errors.push(`localStorage error: ${error.message}`);
  }

  // Determine overall success
  results.success = results.errors.length === 0;

  console.log('[authorizationService] clearUserPermissions completed:', results);
  return results;
}

/**
 * Gets permission details for a specific resource and action
 *
 * @param {string} resource - Resource pattern
 * @param {string} action - Action
 * @param {Object} userPermissions - User permissions cache
 * @returns {Object|null} Permission details or null
 */
export function getPermissionDetails(resource, action, userPermissions) {
  if (!userPermissions?.permissions) {
    return null;
  }

  const permissionKey = `${resource}:${action}`;
  return userPermissions.permissions[permissionKey] || null;
}